import React, { useState } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Paper,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Container,
  Avatar,
  Grid,
  LinearProgress,
  Tooltip,
  StepConnector,
  alpha,
  styled
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SchoolIcon from '@mui/icons-material/School';
import LessonCreator from '../componets/LessonCreator/LessonCreator';
import TestCreator from '../componets/TestCreator/TestCreator';
import { createCourse } from '../services/api';
import { processImagesInContent, compressImage } from '../utils/imageUtils';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  info: '#74b9ff',
  purple: '#6c5ce7',
  orange: '#e17055',
  dark: '#2d3436',
  light: '#dfe6e9'
};

// Colores por categoría
const CATEGORY_COLORS = {
  'Mantenimiento': { color: COLORS.warning, icon: '🔧' },
  'Producción': { color: COLORS.success, icon: '🏭' },
  'Proceso': { color: COLORS.purple, icon: '⚙️' },
  'Calidad': { color: COLORS.primary, icon: '✅' },
  'General': { color: COLORS.orange, icon: '📖' }
};

// Styled Step Connector
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: '#e0e0e0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
  '&.Mui-active .MuiStepConnector-line': {
    background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
    borderColor: 'transparent',
    height: 3,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
    borderColor: 'transparent',
    height: 3,
  },
}));

const CourseCreator = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    coverImage: null,
    coursePdfs: [],
    courseImages: [],
    courseVideos: [],
    lessons: [],
    test: {
      questions: [],
      config: {
        questions_per_test: 10,
        rotateQuestions: true,
        requireAllDifficulties: false
      }
    },
    min_passing_score: 70,
    category: '',
  });
  
  const categories = ['Mantenimiento', 'Producción', 'Proceso', 'Calidad', 'General'];
  
  const steps = [
    { label: 'Información', icon: <InfoIcon /> },
    { label: 'Lecciones', icon: <MenuBookIcon /> },
    { label: 'Test Final', icon: <QuizIcon /> },
    { label: 'Configuración', icon: <SettingsIcon /> },
    { label: 'Revisión', icon: <CheckCircleIcon /> }
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCourseData({ ...courseData, [name]: value });
  };

  const handleFileChange = async (event, fileType) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      if (fileType === 'coverImage') {
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = async () => {
          const compressedImage = await compressImage(reader.result);
          setCourseData({ ...courseData, coverImage: compressedImage });
        };
        reader.readAsDataURL(file);
      } else {
        const newFiles = files.map(file => ({ file, name: file.originalName || file.name }));
        setCourseData(prevData => ({
          ...prevData,
          [fileType]: [...prevData[fileType], ...newFiles]
        }));
      }
    }
  };

  const handleRemoveFile = (fileType, index) => {
    setCourseData({
      ...courseData,
      [fileType]: courseData[fileType].filter((_, i) => i !== index)
    });
  };

  const handleLessonsChange = async (newLessons) => {
    const processedLessons = await Promise.all(newLessons.map(async (lesson) => {
      const processedContent = await processImagesInContent(lesson.content);
      return { ...lesson, content: processedContent };
    }));
    setCourseData({ ...courseData, lessons: processedLessons });
  };

  const handleTestChange = (newTest) => {
    setCourseData(prevData => ({
      ...prevData,
      test: newTest
    }));
  };

  const handleMinPassingScoreChange = (event, newValue) => {
    setCourseData({ ...courseData, min_passing_score: newValue });
  };

  const handleCategoryChange = (event) => {
    setCourseData({ ...courseData, category: event.target.value });
  };

  const handleSubmit = async () => {
    try {
      if (!courseData.title.trim()) {
        alert('El título del curso es obligatorio');
        return;
      }
  
      if (!courseData.category) {
        alert('La categoría del curso es obligatoria');
        return;
      }
  
      const formData = new FormData();
      formData.append('title', courseData.title.trim());
      formData.append('description', courseData.description.trim());
      formData.append('min_passing_score', courseData.min_passing_score);
      formData.append('category', courseData.category);
  
      if (courseData.coverImage) {
        const blob = await fetch(courseData.coverImage).then(r => r.blob());
        formData.append('coverImage', blob, 'cover.jpg');
      }
  
      courseData.coursePdfs.forEach((pdfFile) => {
        formData.append('coursePdf', pdfFile.file, pdfFile.name);
      });
  
      courseData.courseImages.forEach((imageFile) => {
        formData.append('courseImage', imageFile.file, imageFile.name);
      });
  
      courseData.courseVideos.forEach((videoFile) => {
        formData.append('courseVideo', videoFile.file, videoFile.name);
      });
  
      formData.append('lessons', JSON.stringify(courseData.lessons));
      
      const testData = {
        questions: courseData.test.questions.map(question => ({
          question: question.question,
          options: question.options,
          correctAnswer: question.correctAnswer,
          difficulty: question.difficulty
        })),
        config: {
          questions_per_test: courseData.test.config.questions_per_test,
          rotateQuestions: courseData.test.config.rotateQuestions,
          requireAllDifficulties: courseData.test.config.requireAllDifficulties
        }
      };
      formData.append('test', JSON.stringify(testData));
  
      await createCourse(formData);
      alert('Curso creado con éxito');
      
      setCourseData({
        title: '',
        description: '',
        coverImage: null,
        coursePdfs: [],
        courseImages: [],
        courseVideos: [],
        lessons: [],
        test: {
          questions: [],
          config: {
            questions_per_test: 10,
            rotateQuestions: true,
            requireAllDifficulties: false
          }
        },
        min_passing_score: 70,
        category: '',
      });
      setActiveStep(0);
    } catch (error) {
      console.error('Error al crear el curso:', error);
      alert('Error al crear el curso. Por favor, inténtelo de nuevo.');
    }
  };

  const renderFileList = (fileType, label, icon, color) => (
    <Box sx={{ mt: 2 }}>
      {courseData[fileType].length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.dark, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            {label} ({courseData[fileType].length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {courseData[fileType].map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => handleRemoveFile(fileType, index)}
                sx={{
                  bgcolor: alpha(color, 0.1),
                  color: color,
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': { color: color }
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            {/* Header del paso */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <InfoIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Información Básica
                </Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>
                  Define el título, descripción y materiales del curso
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título del curso"
                  name="title"
                  value={courseData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: Buenas Prácticas de Manufactura (BPM)"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: COLORS.primary },
                      '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Descripción del curso"
                  name="description"
                  value={courseData.description}
                  onChange={handleInputChange}
                  placeholder="Describe brevemente el contenido y objetivos del curso..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: COLORS.primary },
                      '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Categoría del curso</InputLabel>
                  <Select
                    label="Categoría del curso"
                    name="category"
                    value={courseData.category}
                    onChange={handleCategoryChange}
                    required
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="" disabled>
                      <em>Seleccione una categoría</em>
                    </MenuItem>
                    {categories.map((category) => {
                      const catConfig = CATEGORY_COLORS[category];
                      return (
                        <MenuItem key={category} value={category}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{catConfig?.icon}</span>
                            <Typography>{category}</Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                {courseData.category && (
                  <Chip
                    label={`${CATEGORY_COLORS[courseData.category]?.icon} ${courseData.category}`}
                    sx={{
                      bgcolor: alpha(CATEGORY_COLORS[courseData.category]?.color, 0.15),
                      color: CATEGORY_COLORS[courseData.category]?.color,
                      fontWeight: 700,
                      fontSize: '1rem',
                      py: 2.5,
                      px: 1
                    }}
                  />
                )}
              </Grid>
            </Grid>

            {/* Archivos adjuntos */}
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                border: '2px dashed #e0e0e0',
                bgcolor: alpha(COLORS.primary, 0.02)
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2 }}>
                Archivos Adjuntos
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Imagen de portada */}
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="cover-image-upload"
                  type="file"
                  onChange={(e) => handleFileChange(e, 'coverImage')}
                />
                <label htmlFor="cover-image-upload">
                  <Button 
                    variant="outlined" 
                    component="span"
                    startIcon={<ImageIcon />}
                    sx={{
                      borderColor: COLORS.purple,
                      color: COLORS.purple,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': { borderColor: COLORS.purple, bgcolor: alpha(COLORS.purple, 0.08) }
                    }}
                  >
                    Portada
                  </Button>
                </label>

                {/* PDFs */}
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="course-pdf-upload"
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(e, 'coursePdfs')}
                />
                <label htmlFor="course-pdf-upload">
                  <Button 
                    variant="outlined" 
                    component="span"
                    startIcon={<PictureAsPdfIcon />}
                    sx={{
                      borderColor: COLORS.error,
                      color: COLORS.error,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': { borderColor: COLORS.error, bgcolor: alpha(COLORS.error, 0.08) }
                    }}
                  >
                    PDFs
                  </Button>
                </label>

                {/* Imágenes */}
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="course-image-upload"
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(e, 'courseImages')}
                />
                <label htmlFor="course-image-upload">
                  <Button 
                    variant="outlined" 
                    component="span"
                    startIcon={<ImageIcon />}
                    sx={{
                      borderColor: COLORS.success,
                      color: COLORS.success,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': { borderColor: COLORS.success, bgcolor: alpha(COLORS.success, 0.08) }
                    }}
                  >
                    Imágenes
                  </Button>
                </label>

                {/* Videos */}
                <input
                  accept="video/*"
                  style={{ display: 'none' }}
                  id="course-video-upload"
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(e, 'courseVideos')}
                />
                <label htmlFor="course-video-upload">
                  <Button 
                    variant="outlined" 
                    component="span"
                    startIcon={<VideoLibraryIcon />}
                    sx={{
                      borderColor: COLORS.primary,
                      color: COLORS.primary,
                      borderRadius: 2,
                      fontWeight: 600,
                      '&:hover': { borderColor: COLORS.primary, bgcolor: alpha(COLORS.primary, 0.08) }
                    }}
                  >
                    Videos
                  </Button>
                </label>
              </Box>

              {/* Preview imagen portada */}
              {courseData.coverImage && (
                <Box sx={{ mt: 3, position: 'relative', display: 'inline-block' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.dark, mb: 1 }}>
                    Imagen de portada:
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img 
                      src={courseData.coverImage} 
                      alt="Course cover" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px',
                        borderRadius: 8,
                        border: '2px solid #e0e0e0'
                      }} 
                    />
                    <IconButton 
                      onClick={() => setCourseData({ ...courseData, coverImage: null })}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: COLORS.error,
                        color: 'white',
                        '&:hover': { bgcolor: alpha(COLORS.error, 0.8) }
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {renderFileList('coursePdfs', 'PDFs adjuntos', <PictureAsPdfIcon sx={{ fontSize: 18 }} />, COLORS.error)}
              {renderFileList('courseImages', 'Imágenes adjuntas', <ImageIcon sx={{ fontSize: 18 }} />, COLORS.success)}
              {renderFileList('courseVideos', 'Videos adjuntos', <VideoLibraryIcon sx={{ fontSize: 18 }} />, COLORS.primary)}
            </Paper>
          </Box>
        );

      case 1:
        return (
          <LessonCreator
            lessons={courseData.lessons}
            onChange={handleLessonsChange}
          />
        );

      case 2:
        return (
          <TestCreator
            test={courseData.test}
            onChange={handleTestChange}
          />
        );

      case 3:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SettingsIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Configuración del Test
                </Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>
                  Define la puntuación mínima para aprobar
                </Typography>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '2px solid #e0e0e0'
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>
                Puntuación mínima para aprobar
              </Typography>
              <Typography variant="body2" sx={{ color: '#636e72', mb: 3 }}>
                Los usuarios deberán obtener al menos esta puntuación para recibir su certificado
              </Typography>
              
              <Box sx={{ px: 2 }}>
                <Slider
                  value={courseData.min_passing_score}
                  onChange={handleMinPassingScoreChange}
                  valueLabelDisplay="on"
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 70, label: '70%' },
                    { value: 100, label: '100%' }
                  ]}
                  min={0}
                  max={100}
                  sx={{
                    color: COLORS.primary,
                    height: 8,
                    '& .MuiSlider-thumb': {
                      width: 24,
                      height: 24,
                      backgroundColor: 'white',
                      border: `3px solid ${COLORS.primary}`,
                      '&:hover': {
                        boxShadow: `0 0 0 8px ${alpha(COLORS.primary, 0.16)}`
                      }
                    },
                    '& .MuiSlider-track': {
                      background: `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                      border: 'none'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: '#e0e0e0'
                    },
                    '& .MuiSlider-valueLabel': {
                      background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                      borderRadius: 2,
                      fontWeight: 700
                    }
                  }}
                />
              </Box>

              <Box 
                sx={{ 
                  mt: 4, 
                  p: 3, 
                  borderRadius: 2, 
                  bgcolor: courseData.min_passing_score >= 70 
                    ? alpha(COLORS.success, 0.1) 
                    : alpha(COLORS.warning, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: courseData.min_passing_score >= 70 ? COLORS.success : COLORS.warning,
                    fontWeight: 800,
                    fontSize: '1.2rem'
                  }}
                >
                  {courseData.min_passing_score}%
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Puntuación mínima: {courseData.min_passing_score}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#636e72' }}>
                    {courseData.min_passing_score >= 70 
                      ? 'Nivel recomendado para formación profesional'
                      : 'Considera aumentar el umbral para mayor rigor'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Revisión Final
                </Typography>
                <Typography variant="body2" sx={{ color: '#636e72' }}>
                  Verifica que toda la información sea correcta antes de crear el curso
                </Typography>
              </Box>
            </Box>

            {/* Resumen general */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '2px solid #e0e0e0',
                mb: 3
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.dark, mb: 1 }}>
                    {courseData.title || 'Sin título'}
                  </Typography>
                  <Typography sx={{ color: '#636e72', mb: 2 }}>
                    {courseData.description || 'Sin descripción'}
                  </Typography>
                  {courseData.category && (
                    <Chip
                      label={`${CATEGORY_COLORS[courseData.category]?.icon} ${courseData.category}`}
                      sx={{
                        bgcolor: alpha(CATEGORY_COLORS[courseData.category]?.color, 0.15),
                        color: CATEGORY_COLORS[courseData.category]?.color,
                        fontWeight: 700
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  {courseData.coverImage && (
                    <img 
                      src={courseData.coverImage} 
                      alt="Course cover" 
                      style={{ 
                        width: '100%',
                        maxHeight: '150px',
                        objectFit: 'cover',
                        borderRadius: 8
                      }} 
                    />
                  )}
                </Grid>
              </Grid>

              {/* Stats */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                <Chip
                  icon={<MenuBookIcon />}
                  label={`${courseData.lessons.length} lecciones`}
                  sx={{ bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.primary } }}
                />
                <Chip
                  icon={<QuizIcon />}
                  label={`${courseData.test.questions.length} preguntas`}
                  sx={{ bgcolor: alpha(COLORS.purple, 0.15), color: COLORS.purple, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.purple } }}
                />
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Mín. ${courseData.min_passing_score}%`}
                  sx={{ bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.success } }}
                />
                {courseData.coursePdfs.length > 0 && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label={`${courseData.coursePdfs.length} PDFs`}
                    sx={{ bgcolor: alpha(COLORS.error, 0.15), color: COLORS.error, fontWeight: 600, '& .MuiChip-icon': { color: COLORS.error } }}
                  />
                )}
              </Box>
            </Paper>

            {/* Lecciones */}
            <Accordion 
              elevation={0}
              sx={{ 
                borderRadius: '12px !important', 
                border: '2px solid #e0e0e0',
                mb: 2,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <MenuBookIcon sx={{ color: COLORS.primary }} />
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Lecciones ({courseData.lessons.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {courseData.lessons.map((lesson, index) => (
                  <Accordion key={index} elevation={0} sx={{ bgcolor: '#f8fffe', mb: 1, borderRadius: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(COLORS.primary, 0.15), color: COLORS.primary, fontSize: '0.8rem', fontWeight: 700 }}>
                          {index + 1}
                        </Avatar>
                        <Typography sx={{ fontWeight: 600 }}>{lesson.title}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box 
                        sx={{ 
                          '& img': { maxWidth: '100%' },
                          '& p': { mb: 1 }
                        }}
                        dangerouslySetInnerHTML={{ __html: lesson.content }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Test */}
            <Accordion 
              elevation={0}
              sx={{ 
                borderRadius: '12px !important', 
                border: '2px solid #e0e0e0',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <QuizIcon sx={{ color: COLORS.purple }} />
                  <Typography sx={{ fontWeight: 700, color: COLORS.dark }}>
                    Test Final ({courseData.test.questions.length} preguntas)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Config */}
                <Box sx={{ mb: 3, p: 2, bgcolor: alpha(COLORS.purple, 0.05), borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>
                    Configuración
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${courseData.test.config.questions_per_test} preguntas por test`} size="small" />
                    <Chip label={courseData.test.config.rotateQuestions ? 'Rotación activada' : 'Sin rotación'} size="small" />
                  </Box>
                </Box>

                {/* Distribución */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark, mb: 1 }}>
                    Distribución por dificultad
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={`Básico: ${courseData.test.questions.filter(q => q.difficulty === 'basico').length}`}
                      sx={{ bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600 }}
                    />
                    <Chip 
                      label={`Intermedio: ${courseData.test.questions.filter(q => q.difficulty === 'intermedio').length}`}
                      sx={{ bgcolor: alpha(COLORS.warning, 0.15), color: COLORS.warning, fontWeight: 600 }}
                    />
                    <Chip 
                      label={`Avanzado: ${courseData.test.questions.filter(q => q.difficulty === 'avanzado').length}`}
                      sx={{ bgcolor: alpha(COLORS.error, 0.15), color: COLORS.error, fontWeight: 600 }}
                    />
                  </Box>
                </Box>

                {/* Preguntas */}
                {courseData.test.questions.map((question, index) => (
                  <Accordion key={index} elevation={0} sx={{ bgcolor: '#f8fffe', mb: 1, borderRadius: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 2 }}>
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>
                          {index + 1}. {question.question}
                        </Typography>
                        <Chip 
                          label={question.difficulty || 'básico'}
                          size="small"
                          sx={{
                            bgcolor: alpha(
                              question.difficulty === 'avanzado' ? COLORS.error :
                              question.difficulty === 'intermedio' ? COLORS.warning : COLORS.success, 0.15
                            ),
                            color: question.difficulty === 'avanzado' ? COLORS.error :
                                   question.difficulty === 'intermedio' ? COLORS.warning : COLORS.success,
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {question.options.map((option, optIndex) => (
                          <ListItem key={optIndex}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {option === question.correctAnswer ? 
                                <CheckCircleOutlineIcon sx={{ color: COLORS.success }} /> : 
                                <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} />
                              }
                            </ListItemIcon>
                            <ListItemText 
                              primary={option}
                              primaryTypographyProps={{
                                sx: { 
                                  fontWeight: option === question.correctAnswer ? 600 : 400,
                                  color: option === question.correctAnswer ? COLORS.success : COLORS.dark
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        );

      default:
        return 'Paso desconocido';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fffe', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: `0 20px 60px ${alpha(COLORS.gradient1, 0.3)}`
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SchoolIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 900, letterSpacing: -0.5 }}>
                Crear Nuevo Curso
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                Paso {activeStep + 1} de {steps.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Stepper */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '2px solid #e0e0e0'
          }}
        >
          <Stepper activeStep={activeStep} connector={<CustomConnector />}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: index <= activeStep 
                          ? `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
                          : '#e0e0e0',
                        color: index <= activeStep ? 'white' : '#9e9e9e',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {index < activeStep ? <CheckCircleIcon /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography 
                    sx={{ 
                      fontWeight: index === activeStep ? 700 : 500,
                      color: index === activeStep ? COLORS.primary : '#636e72'
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Content */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            mb: 3
          }}
        >
          {renderStepContent(activeStep)}
        </Paper>

        {/* Navigation */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: '2px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{
              borderColor: COLORS.primary,
              color: COLORS.primary,
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                borderColor: COLORS.primary,
                bgcolor: alpha(COLORS.primary, 0.08)
              },
              '&.Mui-disabled': {
                borderColor: '#e0e0e0',
                color: '#bdbdbd'
              }
            }}
          >
            Atrás
          </Button>

          {/* Indicador central */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: index === activeStep ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: index <= activeStep 
                    ? `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
                    : '#e0e0e0',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Box>

          {activeStep === steps.length - 1 ? (
            <Button 
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                py: 1.5,
                boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
                }
              }}
            >
              Crear Curso
            </Button>
          ) : (
            <Button 
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
                }
              }}
            >
              Siguiente
            </Button>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default CourseCreator;